import { AbstractBuildstep } from "@project/emerge";
import type {
  AbstractPartDefinition,
  DragConstraint,
  DragUpdateOptions,
  DragUpdateResult,
  SnapAnchor,
} from "@project/emerge-types";
import { Sides } from "@project/emerge-types";
import { getPianoHingeSegments } from "@project/pricing";
import filter from "lodash/filter";
import flatten from "lodash/flatten";
import groupBy from "lodash/groupBy";
import last from "lodash/last";
import map from "lodash/map";
import mapValues from "lodash/mapValues";
import * as THREE from "three"; // Use namespace import
import { ConnectorCategory } from "../enum/ConnectorType";
import { EdgeIndex } from "../enum/EdgeIndex";
import { EdgeType } from "../enum/EdgeType";
import { LTCaseUserOption } from "../enum/LTCaseUserOption";
import { MiterType } from "../enum/MiterType";
import { OrientationOnRect } from "../enum/OrientationOnRect";
import { LTPartType } from "../enum/PartType";
import { VariableOptionType } from "../enum/VariableOptionType";
import type { ConnectorInfos } from "../interfaces/ConnectorInfos";
import type { ConnectorSetInfos } from "../interfaces/ConnectorSetInfos";
import type { FlightCaseBlueprint } from "../interfaces/FlightCaseBlueprint";
import type { FlightCaseSettings } from "../interfaces/FlightCaseSettings";
import type { HingeType } from "../interfaces/HingeType";
import type { LockType } from "../interfaces/LockType";
import type { PianoHingeType } from "../interfaces/PianoHingeType";
import type { ProfileProperties } from "../interfaces/ProfileProperties";
import type { PianoHingeDefinition } from "../parts/PianoHingeDefinition";
import type { ProfileDefinition } from "../parts/ProfileDefinition";
import { compileConnectorSet } from "../structure/ConnectorSetInfos";
import { EdgeInfos } from "../structure/EdgeInfos";
import { DefaultGroupName } from "../structure/Rect";
import { RectPropertiesBySide } from "../structure/RectProperties";
import type { VariableOptions } from "../types/VariableOptions";
import { OptionDBInstance } from "../user_options/options_db";
import {
  type ConnectorMarginAxisRange,
  computeConnectorMarginRangeFromDragContext,
  intersectConnectorMarginRanges,
  marginFromConnectorPlacement,
} from "../utils/connectorMarginDragRange";
import {
  clampRoundToDragGrid,
  connectorPositionFromMargin,
  gridStepFromOptions,
} from "../utils/dragGrid";
import type { HingeDefinition } from "./HingeDefinition";
import type { LockDefinition } from "./LockDefinition";

function isConnectorSetInfos(o: VariableOptions): o is ConnectorSetInfos {
  return (
    o.variableOptionType === VariableOptionType.HINGE_SET ||
    o.variableOptionType === VariableOptionType.LOCK_SET
  );
}

/** Default Klavierband width in mm when option has width 0 (6 segments = 6 × 25.4, matches old mesh clone size). */
const DEFAULT_PIANO_HINGE_WIDTH_MM = 152.4;

/** Resolve piano hinge option from Klavierband (PianoHingeType) options. */
function getPianoHingeOption():
  | (PianoHingeType & {
      meshes?: Partial<Record<"top" | "bottom", unknown>>;
    })
  | null {
  const fromPianoHingeKind = OptionDBInstance.getAllOfKind<PianoHingeType>(
    LTCaseUserOption.PianoHingeType,
  );
  if (fromPianoHingeKind && fromPianoHingeKind.length > 0) {
    return fromPianoHingeKind[0] as PianoHingeType & {
      meshes?: Partial<Record<"top" | "bottom", unknown>>;
    };
  }
  return null;
}

// No local core type; we intersect concrete definitions with AbstractPartDefinition

// --- Dependency Data Interfaces ---

interface BaseProfileStepDependency {
  partId: string;
  groupName: string;
  buildstepName: string; // Keep buildstep name for easy assignment
}

interface ConnectorDependencyData extends BaseProfileStepDependency {
  partKind: "Connector";
  type: LTPartType.Hinge | LTPartType.Lock;
  connectorType: ConnectorCategory; // Hinge or Lock
  partSelection: HingeType | LockType; // Virtual option with specific mesh
  position: THREE.Vector3; // Pre-offset position
  flip: boolean;
  isTop: boolean;
  hasBothMeshes: boolean; // Whether the original connector had both top and bottom meshes
  edgeSide: Sides;
  edgeIsVertical: boolean;
  edgeIndex: number;
  edgeInverted: boolean;
  edgeNormalDir: THREE.Vector3; // Needed for final offset calc
  // Settings needed for final calculations
  settingsProfileStrength: number;
  settingsProfileExtra: number;
  settingsProfileInverted: boolean;
  // Drag constraint data
  sourceOptionUuid: string; // Links back to variableOptions entry
  edgeStartPos: THREE.Vector3; // Edge start for drag bounds
  edgeEndPos: THREE.Vector3; // Edge end for drag bounds
  originalEdgeStartPos: THREE.Vector3; // Original edge start for margin calculation
  originalEdgeEndPos: THREE.Vector3; // Original edge end for margin calculation
  edgeDirection: THREE.Vector3; // Edge direction for drag axis
  edgeLength: number; // Edge length for position calculation
  originalEdgeLength: number; // Original edge length for margin calculation
  // Canonical reference-edge frame (firstRect LEFT/BOTTOM edge) matching
  // Rect.resolvePosition1d. The reverse drag math must invert that exact
  // placement, so it uses this frame instead of the connector edge's own
  // direction/endpoints, which can run opposite for the lid leaf or mirrored
  // sides and would flip the margin sign.
  refEdgeDirection: THREE.Vector3;
  refOriginalStartPos: THREE.Vector3;
  refOriginalEndPos: THREE.Vector3;
  refOriginalEdgeLength: number;
  n: number; // Interpolation factor
  /** Flat-mirror copy: calcPosition flipped its margin (CENTER) / swapped its
   * START/END anchor, so the reverse drag math must undo that for count===1. */
  mirrored: boolean;
  allConnectorPositions: THREE.Vector3[]; // Other connector positions for snap anchors
  // Angle section padding (to prevent overlap)
  angleSectionDepth: number;
  /** Lid mesh copy (`isTop`); bottom/base mesh is shown in the plan. */
  dontShowInPlan: boolean;
}

/** Dependency data for one piano-hinge part (top or bottom). Same orientation/side/rotation as hinges; renderer places mesh at each position. */
interface PianoHingeSegmentDependencyData extends BaseProfileStepDependency {
  partKind: "PianoHingeSegment";
  positions: THREE.Vector3[];
  totalSegments: number;
  totalLength: number;
  invert: boolean;
  /** Same as hinges: edge side for rotation (edge.rects[0].side). */
  edgeSide: Sides;
  edgeDir: "x" | "y";
  settingsProfileStrength: number;
  edgeIsVertical: boolean;
  isTop: boolean;
  edgeIndex: number;
  flip: boolean;
}

interface ProfileDependencyData extends BaseProfileStepDependency {
  partKind: "Profile";
  type: LTPartType.Profile;
  points: THREE.Vector3[];
  cutReference: Sides; // Side
  inverse: boolean; // from edge.inverted
  miter: MiterType;
  partSelection: ProfileProperties["partSelection"]; // From settings
  // Drag constraint data for closing profiles
  cutGroupName: string | null; // Links to settings.cuts entry
  cutNormal: THREE.Vector3 | null; // Direction perpendicular to edge (for drag axis)
  edgeCenter: THREE.Vector3; // Center of the edge for drag bounds
  edgeDirection: THREE.Vector3; // Direction along the edge
  edgeLength: number; // Length of the edge
  currentCutOffset: number; // Current cut offset for reference
  profileExtra: number; // Profile extra setting
  // Minimum offset from case border for closing profiles (in mm)
  minOffsetFromBorder: number;
  // Maximum offset based on case geometry (in mm)
  maxCutOffset: number;
}

type ProfileStepDependencyData =
  | ConnectorDependencyData
  | PianoHingeSegmentDependencyData
  | ProfileDependencyData;

// --- Refactored Build Step ---

export class ProfileBuildStep extends AbstractBuildstep<
  FlightCaseBlueprint,
  FlightCaseSettings,
  AbstractPartDefinition<string>,
  ProfileStepDependencyData // Specify the dependency data type
> {
  constructor() {
    super("ProfileBuilder"); // Consistent name
  }

  public selectDependencies(
    blueprint: FlightCaseBlueprint,
    settings: FlightCaseSettings,
  ): Map<string, ProfileStepDependencyData> {
    const dependencies = new Map<string, ProfileStepDependencyData>();
    let pianoHingeCounter = 0;
    let profileCounter = 0;

    // build index to get edges by groupname and cutReference
    const groupedByGroup = groupBy(blueprint.rects, "group");
    const edgesByGroupAndReference = mapValues(groupedByGroup, (rects) =>
      groupBy(
        filter(flatten(map(rects, (r) => r.edges)), {
          type: EdgeType.FROMCUT,
        }),
        "cutReference",
      ),
    );

    const {
      strength: profileStrength,
      extra: profileExtra,
      inverted: profileInverted,
    } = settings.exteriorOptions.profiles;

    // Get angle section depth for constraint padding
    const angleSectionDepth =
      settings.exteriorOptions.angleSections?.depth ?? 0;

    // Minimum offset from case border for closing profiles (in mm)
    const minOffsetFromBorder = 50;

    // Klavierband width for spacing: parts sit directly next to each other (Breite = Abstand)
    const pianoHingeOption = getPianoHingeOption();
    const pianoHingeWidthMm =
      pianoHingeOption && pianoHingeOption.width > 0
        ? pianoHingeOption.width
        : DEFAULT_PIANO_HINGE_WIDTH_MM;

    // --- Helper Functions (Adapted from execute) ---

    type EdgeConnector = EdgeInfos["connectors"][number];

    const connectorPartId = (
      connector: EdgeConnector,
      edge: EdgeInfos,
      edgeSide: Sides,
      groupName: string,
    ): string => {
      // Stable across rebuilds: array index and a running counter both change when
      // margin/count updates reorder or re-emit parts, which remounts DraggablePart
      // mid-drag and breaks snapDisplayDuringDrag for multi-connector sets.
      const slotKey = Math.round(connector.n * 1_000_000);
      return `connector_${connector.sourceOptionUuid}_${slotKey}_${connector.mirrored ? "m" : "p"}_${edgeSide}_${edge.index}_${groupName}`;
    };

    const meshKeyForConnector = (
      connector: EdgeConnector,
      isTop: boolean,
    ): "top" | "bottom" => {
      if (connector.flip) {
        return isTop ? "bottom" : "top";
      }
      return isTop ? "top" : "bottom";
    };

    const resolveConnectorMesh = (
      connector: EdgeConnector,
      meshKey: "top" | "bottom",
    ): unknown => {
      return (
        connector.partSelection.meshes?.[meshKey] ||
        connector.partSelection.meshes?.[meshKey === "top" ? "bottom" : "top"]
      );
    };

    const createConnectorDependency = (
      edge: EdgeInfos,
      connector: EdgeConnector,
      groupName: string,
      partId: string,
      angleSectionPadding: number,
      allConnectorPositions: THREE.Vector3[],
    ): ConnectorDependencyData | null => {
      const cutRef = edge.cut?.reference;
      if (cutRef === undefined) return null;

      const isTop = cutRef !== edge.cutReference;
      const finalMesh = resolveConnectorMesh(
        connector,
        meshKeyForConnector(connector, isTop),
      );

      if (!finalMesh) {
        console.warn(
          `Connector ${connector.partSelection.name} has no mesh key=${meshKeyForConnector(connector, isTop)} side=${edge.sides[0]} z=${connector.position.z}`,
        );
        return null;
      }

      const firstRect = edge.rects[0];
      if (!firstRect) return null;

      // Reconstruct the exact frame Rect.resolvePosition1d uses to PLACE this
      // connector, so the reverse drag math can invert it. resolvePosition1d
      // works on firstRect's LEFT (vertical) / BOTTOM (horizontal) reference
      // edge, measuring along the rect's axis direction from the endpoint that
      // comes first in that direction. Each leaf reconstructs this from its own
      // firstRect, which makes the direction correct for both the lid/body
      // leaves and mirrored sides.
      const refIsVertical =
        edge.orientationOnRect === OrientationOnRect.VERTICAL;
      const refEdge =
        firstRect.edges[refIsVertical ? EdgeIndex.LEFT : EdgeIndex.BOTTOM];
      const refDir = (
        refIsVertical
          ? firstRect.properties.verticalDir
          : firstRect.properties.horizontalDir
      ).clone();
      const refAligned = refEdge ? refEdge.direction.dot(refDir) > 0 : true;
      const refStartEndpoint = refAligned ? refEdge?.start : refEdge?.end;
      const refEndEndpoint = refAligned ? refEdge?.end : refEdge?.start;
      const refOriginalStartPos =
        refStartEndpoint?.originalOuterPos.clone() ??
        edge.start.originalOuterPos.clone();
      const refOriginalEndPos =
        refEndEndpoint?.originalOuterPos.clone() ??
        edge.end.originalOuterPos.clone();
      const refOriginalEdgeLength =
        refEdge?.originalEdgeLength ?? edge.originalEdgeLength;

      const meshRecord = finalMesh as Record<string, unknown>;
      const selectionRecord = connector.partSelection as unknown as Record<
        string,
        unknown
      >;
      const finalPartSelection = {
        ...connector.partSelection,
        meshes: { default: finalMesh },
        cutouts: meshRecord.cutouts ?? selectionRecord.cutouts,
      };

      return {
        partId,
        partKind: "Connector",
        groupName,
        buildstepName: this.buildstepName,
        type:
          connector.type === ConnectorCategory.Hinge
            ? LTPartType.Hinge
            : LTPartType.Lock,
        connectorType: connector.type,
        partSelection: finalPartSelection as HingeType | LockType,
        position: connector.position.clone(),
        flip: connector.flip,
        isTop,
        hasBothMeshes: false,
        edgeSide: firstRect.side,
        edgeIsVertical: edge.orientationOnRect === OrientationOnRect.VERTICAL,
        edgeIndex: edge.index,
        edgeInverted: edge.inverted,
        edgeNormalDir: edge.inwards.clone(),
        settingsProfileStrength: profileStrength,
        settingsProfileExtra: profileExtra,
        settingsProfileInverted: profileInverted,
        sourceOptionUuid: connector.sourceOptionUuid,
        edgeStartPos: edge.start.outerPos.clone(),
        edgeEndPos: edge.end.outerPos.clone(),
        originalEdgeStartPos: edge.start.originalOuterPos.clone(),
        originalEdgeEndPos: edge.end.originalOuterPos.clone(),
        edgeDirection: edge.direction.clone(),
        edgeLength: edge.outerLength,
        originalEdgeLength: edge.originalEdgeLength,
        refEdgeDirection: refDir,
        refOriginalStartPos,
        refOriginalEndPos,
        refOriginalEdgeLength,
        n: connector.n,
        mirrored: connector.mirrored,
        allConnectorPositions,
        angleSectionDepth: angleSectionPadding,
        dontShowInPlan: isTop,
      };
    };

    const calculateConnectorDependencies = (
      edges: EdgeInfos[],
      groupName: string,
      angleSectionPadding: number,
    ): ConnectorDependencyData[] => {
      const connectorDeps: ConnectorDependencyData[] = [];
      for (const edge of edges) {
        const allConnectorPositions = edge.connectors.map((c) =>
          c.position.clone(),
        );

        for (const connector of edge.connectors) {
          if (!connector) continue;
          const firstRect = edge.rects[0];
          if (!firstRect) continue;
          const edgeSide = firstRect.side;
          const partId = connectorPartId(connector, edge, edgeSide, groupName);
          const dep = createConnectorDependency(
            edge,
            connector,
            groupName,
            partId,
            angleSectionPadding,
            allConnectorPositions,
          );
          if (!dep) {
            continue;
          }
          connectorDeps.push(dep);
        }
      }
      return connectorDeps;
    };

    const collectPianoHingeSegmentData = (
      connectors: EdgeConnector[],
      bandWidthMm: number,
    ): {
      firstConnector: EdgeConnector | null;
      segmentData: { length: number; position: THREE.Vector3 }[];
    } => {
      const firstConn = connectors[0];
      if (firstConn?.type !== ConnectorCategory.Hinge) {
        return { firstConnector: null, segmentData: [] };
      }

      let firstConnector: EdgeConnector | null = null;
      const segmentData: { length: number; position: THREE.Vector3 }[] = [];
      const phConnectors = filter(
        connectors,
        (c) => (c.partSelection as HingeType).pianoHinge === true,
      );

      for (let c = 0; c < phConnectors.length - 1; c++) {
        const connector1 = phConnectors[c];
        const connector2 = phConnectors[c + 1];
        if (!connector1 || !connector2) continue;
        firstConnector ??= connector1;

        const width =
          (connector1.partSelection as HingeType).width > 0
            ? (connector1.partSelection as HingeType).width
            : 0;
        const pairLength =
          connector1.position.distanceTo(connector2.position) - width - 20;
        const nParts = Math.max(1, Math.floor(pairLength / bandWidthMm));
        const gapCenter = connector1.position
          .clone()
          .lerp(connector2.position, 0.5);
        const edgeDirVec = connector2.position
          .clone()
          .sub(connector1.position)
          .normalize();

        for (let i = 0; i < nParts; i++) {
          let thisPartLength = pairLength;
          if (nParts > 1) {
            thisPartLength =
              i < nParts - 1
                ? bandWidthMm
                : pairLength - (nParts - 1) * bandWidthMm;
          }
          const position = gapCenter
            .clone()
            .add(
              edgeDirVec
                .clone()
                .multiplyScalar((i - (nParts - 1) / 2) * bandWidthMm),
            );
          segmentData.push({ length: thisPartLength, position });
        }
      }

      return { firstConnector, segmentData };
    };

    const calculatePianoHingeDependencies = (
      sortedEdges: EdgeInfos[],
      bandWidthMm: number,
    ): PianoHingeSegmentDependencyData[] => {
      const hingeDeps: PianoHingeSegmentDependencyData[] = [];
      for (const edge of sortedEdges) {
        const { connectors, group } = edge;
        const firstRect = edge.rects[0];
        if (!firstRect) continue;
        const edgeSide = firstRect.side;
        const { firstConnector, segmentData } = collectPianoHingeSegmentData(
          connectors,
          bandWidthMm,
        );
        if (segmentData.length === 0) continue;
        const totalLength = segmentData.reduce((s, d) => s + d.length, 0);
        const totalSegments = getPianoHingeSegments(totalLength);
        const positions = segmentData.map((d) => d.position.clone());
        const flip = firstConnector?.flip ?? false;
        const cutRef = edge.cut?.reference;
        if (cutRef === undefined) continue;
        const isTop = cutRef !== edge.cutReference;
        // Old logic: one part per edge (mesh = top or bottom from isTop), not two parts per edge
        const base: Omit<
          PianoHingeSegmentDependencyData,
          | "partId"
          | "partKind"
          | "positions"
          | "totalSegments"
          | "totalLength"
          | "isTop"
        > = {
          groupName: group,
          buildstepName: this.buildstepName,
          invert: group === DefaultGroupName,
          edgeSide,
          edgeDir: edge.dir === "y" ? "y" : "x",
          settingsProfileStrength: profileStrength,
          edgeIsVertical: edge.orientationOnRect === OrientationOnRect.VERTICAL,
          edgeIndex: edge.index,
          flip,
        };
        hingeDeps.push({
          ...base,
          partId: `pianohinge_${group}_${edgeSide}_${isTop ? "top" : "bottom"}_${pianoHingeCounter++}`,
          partKind: "PianoHingeSegment",
          positions,
          totalSegments,
          totalLength,
          isTop,
        });
      }
      return hingeDeps;
    };

    // Helper: Calculate cut normal direction.
    // `normalDir` points outward from the case; increasing the cut offset moves
    // the seam (and both paired closing profiles, lid + base) INWARD, so the
    // drag axis is the inward direction (-normalDir) for every leaf. This must
    // not depend on the group's `inverted` flag: both profiles sit on the same
    // seam and move together in world space, so flipping the sign for the
    // inverted leaf reversed its drag direction (the reported bug).
    const calculateCutNormal = (
      cut: { reference?: Sides } | null,
    ): THREE.Vector3 | null => {
      if (cut?.reference === undefined) return null;
      const refProps = RectPropertiesBySide[cut.reference];
      if (!refProps) return null;

      return refProps.normalDir.clone().negate();
    };

    // Helper: Determine miter type for a segment
    const determineMiterType = (
      segPoints: THREE.Vector3[],
      sortedEdges: EdgeInfos[],
      isClosedLoop: boolean,
    ): MiterType => {
      if (isClosedLoop) return MiterType.NONE;

      const firstEdge = sortedEdges[0];
      const lastEdge = last(sortedEdges);
      if (!firstEdge || !lastEdge) return MiterType.NONE;

      const startOfChain = firstEdge.start.outerPos;
      const endOfChain = lastEdge.end.outerPos;

      const sp0 = segPoints[0];
      const spL = segPoints.at(-1);
      if (!sp0 || !spL) return MiterType.NONE;

      const isStartOfChain = sp0.distanceTo(startOfChain) < 0.1;
      const isEndOfChain = spL.distanceTo(endOfChain) < 0.1;

      if (isStartOfChain && isEndOfChain) return MiterType.BOTH;
      if (isStartOfChain) return MiterType.START;
      if (isEndOfChain) return MiterType.END;
      return MiterType.NONE;
    };

    interface AtomicSegment {
      points: THREE.Vector3[];
      startsAtEdgeStart: boolean;
      endsAtEdgeEnd: boolean;
      sourceEdge: EdgeInfos; // Track source edge for drag data
    }

    interface ProfileDependencyContext {
      sortedEdges: EdgeInfos[];
      groupName: string;
      inverted: boolean;
      minOffset: number;
      isClosedLoop: boolean;
      cutGroupName: string | null;
      cutNormal: THREE.Vector3 | null;
      currentCutOffset: number;
      maxCutOffset: number;
    }

    const calculateMaxCutOffset = (
      cutNormal: THREE.Vector3 | null,
      minOffset: number,
    ): number => {
      if (!cutNormal || !blueprint.innerDimensions) {
        return 500; // Fallback
      }
      const dims = blueprint.innerDimensions;
      const depthInCutDirection = Math.abs(
        dims.x * cutNormal.x + dims.y * cutNormal.y + dims.z * cutNormal.z,
      );
      return depthInCutDirection - minOffset;
    };

    const generateAtomicSegments = (
      sortedEdges: EdgeInfos[],
    ): AtomicSegment[] => {
      const allSegments: AtomicSegment[] = [];

      for (const edge of sortedEdges) {
        const connectors = edge.cuttingConnectors;
        for (let i = 0; i <= connectors.length; i++) {
          const pStart = edge.getCutPosition(i, false);
          const pEnd = edge.getCutPosition(i + 1, true);

          if (
            pStart.distanceTo(pEnd) < 0.1 ||
            edge.spansConnectorCutout(pStart, pEnd)
          ) {
            continue;
          }

          allSegments.push({
            points: [pStart, pEnd],
            startsAtEdgeStart: i === 0,
            endsAtEdgeEnd: i === connectors.length,
            sourceEdge: edge,
          });

          if (allSegments.length > 500) {
            console.error(
              `ProfileBuildStep: too many segments (${allSegments.length}), aborting edge`,
            );
            break;
          }
        }
      }

      return allSegments;
    };

    const stitchSegments = (allSegments: AtomicSegment[]): AtomicSegment[] => {
      if (allSegments.length === 0) {
        return [];
      }

      const mergedSegments: AtomicSegment[] = [];
      const firstSeg = allSegments[0];
      if (!firstSeg) {
        return [];
      }
      let current = firstSeg;

      for (let k = 1; k < allSegments.length; k++) {
        const next = allSegments[k];
        if (!next) continue;

        if (current.endsAtEdgeEnd && next.startsAtEdgeStart) {
          current.points.push(...next.points.slice(1));
          current.endsAtEdgeEnd = next.endsAtEdgeEnd;
        } else {
          mergedSegments.push(current);
          current = next;
        }
      }
      mergedSegments.push(current);

      return mergedSegments;
    };

    const handleClosedLoopWrapAround = (
      mergedSegments: AtomicSegment[],
      isClosedLoop: boolean,
    ): AtomicSegment[] => {
      if (!isClosedLoop || mergedSegments.length <= 1) {
        return mergedSegments;
      }

      const first = mergedSegments[0];
      const lastSeg = mergedSegments.at(-1);
      if (!first || !lastSeg) {
        return mergedSegments;
      }

      if (lastSeg.endsAtEdgeEnd && first.startsAtEdgeStart) {
        lastSeg.points.push(...first.points.slice(1));
        lastSeg.endsAtEdgeEnd = first.endsAtEdgeEnd;
        mergedSegments.shift();
      }

      return mergedSegments;
    };

    const createProfileDependencies = (
      mergedSegments: AtomicSegment[],
      context: ProfileDependencyContext,
    ): ProfileDependencyData[] => {
      const profileDeps: ProfileDependencyData[] = [];
      const firstEdge = context.sortedEdges[0];
      if (!firstEdge) return profileDeps;

      const side = firstEdge.cutReference;

      for (const [idx, seg] of mergedSegments.entries()) {
        const miterType = determineMiterType(
          seg.points,
          context.sortedEdges,
          context.isClosedLoop,
        );
        const partId = `profile_${context.groupName}_${side}_${idx}_${profileCounter++}`;

        const segStart = seg.points[0];
        const segEnd = seg.points.at(-1);
        if (!segStart || !segEnd) continue;
        const edgeCenter = segStart.clone().add(segEnd).multiplyScalar(0.5);
        const edgeDirection = segEnd.clone().sub(segStart).normalize();
        const edgeLength = segStart.distanceTo(segEnd);

        const dep: ProfileDependencyData = {
          partId,
          partKind: "Profile",
          type: LTPartType.Profile,
          groupName: context.groupName,
          buildstepName: this.buildstepName,
          points: seg.points,
          cutReference: side,
          inverse: context.inverted,
          miter: miterType,
          partSelection: settings.exteriorOptions.profiles,
          cutGroupName: context.cutGroupName,
          cutNormal: context.cutNormal,
          edgeCenter,
          edgeDirection,
          edgeLength,
          currentCutOffset: context.currentCutOffset,
          profileExtra,
          minOffsetFromBorder: context.minOffset,
          maxCutOffset: context.maxCutOffset,
        };
        profileDeps.push(dep);
      }

      return profileDeps;
    };

    const calculateProfileDependencies = (
      sortedEdges: EdgeInfos[],
      groupName: string,
      inverted: boolean,
      minOffset: number,
    ): ProfileDependencyData[] => {
      if (!sortedEdges || sortedEdges.length === 0) {
        return [];
      }
      const firstEdge = sortedEdges[0];
      const lastEdge = last(sortedEdges);
      if (!firstEdge || !lastEdge) return [];

      const isClosedLoop = lastEdge.pointsTo(firstEdge);
      const cut = firstEdge.cut;
      const cutGroupName = cut?.groupName ?? null;
      const currentCutOffset = cut?.offset ?? 0;

      const cutNormal = calculateCutNormal(cut);
      const maxCutOffset = calculateMaxCutOffset(cutNormal, minOffset);

      const allSegments = generateAtomicSegments(sortedEdges);
      if (allSegments.length === 0) {
        return [];
      }

      const mergedSegments = stitchSegments(allSegments);
      const finalSegments = handleClosedLoopWrapAround(
        mergedSegments,
        isClosedLoop,
      );

      return createProfileDependencies(finalSegments, {
        sortedEdges,
        groupName,
        inverted,
        minOffset,
        isClosedLoop,
        cutGroupName,
        cutNormal,
        currentCutOffset,
        maxCutOffset,
      });
    };

    // Helper: Determine edge sets from sorted edges
    const determineEdgeSets = (
      sortedEdges: EdgeInfos[],
      edges: EdgeInfos[],
      groupName: string,
      reference: string,
    ): EdgeInfos[][] => {
      if (sortedEdges.length === edges.length && edges.length > 0) {
        return [sortedEdges];
      }
      if (edges.length > 0) {
        console.warn(
          `Edges in group ${groupName} reference ${reference} are not fully connected or empty. Processing individually.`,
        );
        return edges.map((edge) => [edge]);
      }
      console.warn(
        `No edges found for group ${groupName} reference ${reference}. Skipping.`,
      );
      return [];
    };

    // Helper: Process a single edge set and add dependencies
    const processEdgeSet = (
      currentEdgeSet: EdgeInfos[],
      groupName: string,
      inverted: boolean,
    ): void => {
      const connectorDeps = calculateConnectorDependencies(
        currentEdgeSet,
        groupName,
        angleSectionDepth,
      );
      const pianoHingeDeps = calculatePianoHingeDependencies(
        currentEdgeSet,
        pianoHingeWidthMm,
      );
      const profileDeps = calculateProfileDependencies(
        currentEdgeSet,
        groupName,
        inverted,
        minOffsetFromBorder,
      );

      connectorDeps.forEach((dep) => {
        dependencies.set(dep.partId, dep);
      });
      pianoHingeDeps.forEach((dep) => {
        dependencies.set(dep.partId, dep);
      });
      profileDeps.forEach((dep) => {
        dependencies.set(dep.partId, dep);
      });
    };

    // --- Main Loop ---
    let totalIterations = 0;
    const maxTotalIterations = 1000; // Safety limit
    let shouldAbort = false;

    for (const groupName in edgesByGroupAndReference) {
      if (shouldAbort) break;
      for (const reference in edgesByGroupAndReference[groupName]) {
        if (++totalIterations > maxTotalIterations) {
          console.error(
            `ProfileBuildStep: exceeded max iterations (${maxTotalIterations}), aborting`,
          );
          shouldAbort = true;
          break;
        }

        const edgeList = edgesByGroupAndReference[groupName]?.[reference];
        if (!edgeList || edgeList.length === 0) continue;

        const firstEdgeEntry = edgeList[0];
        const inverted = firstEdgeEntry?.inverted ?? false;
        const sortedEdges = EdgeInfos.sortConnectedEdges(edgeList);
        const edgeSets = determineEdgeSets(
          sortedEdges,
          edgeList,
          groupName,
          reference,
        );

        for (const currentEdgeSet of edgeSets) {
          processEdgeSet(currentEdgeSet, groupName, inverted);
        }
      }
    }

    return dependencies;
  }

  public createPart(
    partId: string,
    dependencies: ProfileStepDependencyData,
  ): AbstractPartDefinition<string> {
    switch (dependencies.partKind) {
      case "Connector":
        return this.createConnectorPart(partId, dependencies);
      case "PianoHingeSegment":
        return this.createPianoHingeSegmentPart(
          partId,
          dependencies,
        ) as unknown as AbstractPartDefinition<string>;
      case "Profile":
        return this.createProfilePart(partId, dependencies);
      default: {
        const exhaustiveCheck: never = dependencies;
        throw new Error(`Unknown part kind: ${exhaustiveCheck}`);
      }
    }
  }

  private createConnectorPart(
    partId: string,
    dependencies: ConnectorDependencyData,
  ): LockDefinition | HingeDefinition {
    const {
      settingsProfileStrength,
      settingsProfileExtra,
      settingsProfileInverted,
      edgeInverted,
      edgeIsVertical,
      isTop,
      edgeIndex,
      edgeSide,
      partSelection, // Keep the union type here
      // Drag constraint data
      sourceOptionUuid,
      edgeStartPos,
      edgeEndPos,
      edgeDirection,
      edgeLength,
      n,
      allConnectorPositions,
    } = dependencies;

    // Common calculations
    const e = settingsProfileExtra / 2 - settingsProfileStrength;
    const offset = edgeInverted !== settingsProfileInverted ? -e : e;
    const edgeRectProps = RectPropertiesBySide[edgeSide];
    if (!edgeRectProps) {
      throw new Error(
        `ProfileBuildStep: missing RectPropertiesBySide for edgeSide ${String(edgeSide)}`,
      );
    }
    const outwardOffset = edgeRectProps.normalDir
      .clone()
      .multiplyScalar(settingsProfileStrength);
    let orientation: number = edgeIsVertical ? 90 : 0;

    if (isTop !== edgeIndex < 2) {
      orientation += 180;
    }

    if (dependencies.flip) {
      orientation -= 180;
    }

    // Get connector's own cut size for padding
    const psRec = partSelection as Record<string, unknown>;
    const cutRaw = psRec.cut;
    const connectorCut =
      typeof cutRaw === "number" ? cutRaw : Number(cutRaw) || 0;

    // Shrink edge bounds by angle section depth + half connector's own size
    const totalPadding = dependencies.angleSectionDepth + connectorCut / 2;
    const paddedEdgeStart = edgeStartPos
      .clone()
      .add(edgeDirection.clone().multiplyScalar(totalPadding));
    const paddedEdgeEnd = edgeEndPos
      .clone()
      .sub(edgeDirection.clone().multiplyScalar(totalPadding));

    const finalPartPosition = dependencies.position
      .clone()
      .add(dependencies.edgeNormalDir.clone().multiplyScalar(offset))
      .add(outwardOffset);

    const anchorOnEdge = new THREE.Vector3();
    new THREE.Line3(edgeStartPos, edgeEndPos).closestPointToPoint(
      finalPartPosition,
      true,
      anchorOnEdge,
    );
    const visualOffset = finalPartPosition.clone().sub(anchorOnEdge);

    const withVisualOffset = (pos: THREE.Vector3): [number, number, number] => {
      const p = pos.clone().add(visualOffset);
      return [p.x, p.y, p.z];
    };

    // Build drag constraint for edge movement
    const dragConstraint: DragConstraint = {
      type: "edge",
      axis: [edgeDirection.x, edgeDirection.y, edgeDirection.z],
      bounds: {
        min: [paddedEdgeStart.x, paddedEdgeStart.y, paddedEdgeStart.z],
        max: [paddedEdgeEnd.x, paddedEdgeEnd.y, paddedEdgeEnd.z],
      },
      snapAnchors: [
        // Center of edge (part center, not padded-edge anchor)
        {
          position: withVisualOffset(
            new THREE.Vector3(
              (edgeStartPos.x + edgeEndPos.x) / 2,
              (edgeStartPos.y + edgeEndPos.y) / 2,
              (edgeStartPos.z + edgeEndPos.z) / 2,
            ),
          ),
          label: "Center",
        },
        // Other connectors on same edge (excluding self)
        ...allConnectorPositions
          .filter(
            (pos) => !pos.equals(dependencies.position), // Exclude self
          )
          .map(
            (pos, idx): SnapAnchor => ({
              position: withVisualOffset(pos),
              label: `Connector ${idx + 1}`,
            }),
          ),
      ],
      sourceOptionUuid,
      snapDisplayDuringDrag: true,
      visualOffset: [visualOffset.x, visualOffset.y, visualOffset.z],
    };

    // Reverse drag math (updateSettingsFromConnectorDrag) must invert the exact
    // placement done by Rect.resolvePosition1d. That placement uses the
    // reference-edge frame (refEdgeDirection / refOriginal* derived from
    // firstRect's LEFT/BOTTOM edge), NOT the connector edge's own
    // direction/endpoints. The connector edge can run opposite for the lid leaf
    // or on mirrored sides, which would flip the projected margin sign and make
    // the part drag the wrong way / jump on release. Using the reference frame
    // is correct for every leaf regardless of which mesh the drag starts on.
    const {
      refEdgeDirection,
      refOriginalStartPos,
      refOriginalEndPos,
      refOriginalEdgeLength,
    } = dependencies;

    // Settings context for updateSettingsFromDrag
    const settingsContext: Record<string, unknown> = {
      edgeStartPos: [edgeStartPos.x, edgeStartPos.y, edgeStartPos.z],
      edgeEndPos: [edgeEndPos.x, edgeEndPos.y, edgeEndPos.z],
      originalEdgeStartPos: [
        refOriginalStartPos.x,
        refOriginalStartPos.y,
        refOriginalStartPos.z,
      ],
      originalEdgeEndPos: [
        refOriginalEndPos.x,
        refOriginalEndPos.y,
        refOriginalEndPos.z,
      ],
      edgeLength,
      originalEdgeLength: refOriginalEdgeLength,
      edgeDirection: [
        refEdgeDirection.x,
        refEdgeDirection.y,
        refEdgeDirection.z,
      ],
      n,
      mirrored: dependencies.mirrored,
      // Offset from the padded drag edge to the rendered part center (profile
      // depth + outward). Drag projection stays on the edge; snap adds this back.
      visualOffset: [visualOffset.x, visualOffset.y, visualOffset.z],
    };

    const {
      isTop: _isTop,
      partKind: _partKind,
      connectorType: _connectorType,
      ...connectorDeps
    } = dependencies;

    // Common properties for the return object
    const commonProps = {
      ...connectorDeps,
      id: partId,
      buildstepName: this.buildstepName,
      group: dependencies.groupName,
      mesh: (() => {
        const m = (dependencies.partSelection as Record<string, unknown>).mesh;
        return typeof m === "string" ? m : "";
      })(),
      isSupplyPart: true as const,
      position: finalPartPosition,
      orientation,
      rotation: this.rotateOntoSide(dependencies.edgeSide, orientation),
      scale: 1,
      dontShowInPlan: dependencies.dontShowInPlan,
      distanceFromPanel: settingsProfileStrength,
      flip: dependencies.flip, // Explicitly include flip property for cutout rotation
      dragConstraint,
      settingsContext,
    };

    if (dependencies.type === LTPartType.Hinge) {
      return {
        ...commonProps,
        type: LTPartType.Hinge,
        partSelection: dependencies.partSelection as HingeType,
      } satisfies HingeDefinition;
    } else if (dependencies.type === LTPartType.Lock) {
      return {
        ...commonProps,
        type: LTPartType.Lock,
        partSelection: dependencies.partSelection as LockType,
      } satisfies LockDefinition;
    } else {
      throw new Error(
        `Invalid dependency type encountered for Connector: ${dependencies.type}`,
      );
    }
  }

  /** Creates one PianoHinge part (top or bottom) for a hinge row. Same rotation/orientation as hinges; part.positions = band positions, renderer places mesh at each. */
  private createPianoHingeSegmentPart(
    partId: string,
    dependencies: PianoHingeSegmentDependencyData,
  ): PianoHingeDefinition {
    const pianoHingeOption = getPianoHingeOption();
    if (!pianoHingeOption) {
      throw new Error(
        "ProfileBuildStep: no Klavierband option found. Add an option under 'Klavierband' in the options library.",
      );
    }
    const effectiveFlip = dependencies.flip;
    let meshKey: "top" | "bottom";
    if (effectiveFlip) {
      meshKey = dependencies.isTop ? "bottom" : "top";
    } else {
      meshKey = dependencies.isTop ? "top" : "bottom";
    }
    const sourceMesh =
      pianoHingeOption.meshes?.[meshKey] ??
      pianoHingeOption.meshes?.[meshKey === "top" ? "bottom" : "top"];
    if (!sourceMesh) {
      throw new Error(
        "ProfileBuildStep: Klavierband option has no meshes (top/bottom).",
      );
    }
    const finalPartSelection: PianoHingeType = {
      ...pianoHingeOption,
      totalSegments: dependencies.totalSegments,
      meshes: { default: sourceMesh },
    } as unknown as PianoHingeType;

    // Same orientation and rotation as hinges (createConnectorPart)
    let orientation: number = dependencies.edgeIsVertical ? 90 : 0;
    if (dependencies.isTop !== dependencies.edgeIndex < 2) {
      orientation += 180;
    }
    if (effectiveFlip) {
      orientation -= 180;
    }
    const pianoEdgeRectProps = RectPropertiesBySide[dependencies.edgeSide];
    if (!pianoEdgeRectProps) {
      throw new Error(
        `ProfileBuildStep: missing RectPropertiesBySide for edgeSide ${String(dependencies.edgeSide)}`,
      );
    }
    const outwardOffset = pianoEdgeRectProps.normalDir
      .clone()
      .multiplyScalar(dependencies.settingsProfileStrength);
    const positions = dependencies.positions.map((p) =>
      p.clone().add(outwardOffset),
    );

    return {
      id: partId,
      buildstepName: this.buildstepName,
      group: dependencies.groupName,
      type: LTPartType.PianoHinge,
      partSelection: finalPartSelection,
      positions,
      segments: dependencies.totalSegments,
      orientation,
      rotation: this.rotateOntoSide(dependencies.edgeSide, orientation),
      scale: 1,
      distanceFromPanel: dependencies.settingsProfileStrength,
      flip: effectiveFlip,
      isSupplyPart: true,
      dontShowInPlan: dependencies.isTop,
    } satisfies PianoHingeDefinition;
  }

  private createProfilePart(
    partId: string,
    dependencies: ProfileDependencyData,
  ): ProfileDefinition {
    const {
      cutReference,
      points,
      groupName,
      cutGroupName,
      cutNormal,
      edgeCenter,
      edgeDirection,
      edgeLength,
      currentCutOffset,
      profileExtra: depProfileExtra,
      minOffsetFromBorder,
      maxCutOffset,
      inverse,
      ...restDeps
    } = dependencies;
    const side = cutReference;
    const rectProps = RectPropertiesBySide[side];
    if (!rectProps) {
      throw new Error(
        `ProfileBuildStep: missing RectPropertiesBySide for ${String(side)}`,
      );
    }
    const { horizontal, vertical, normal } = rectProps;

    // Calculate center as middle of middle segment in GEOMETRY space
    // This ensures renderer and buildstep use the same centering logic
    // Must match renderer's ccw-based reversal to get the same middle segment
    const ccw = ![Sides.TOP, Sides.BACK, Sides.RIGHT].includes(side);
    const orderedPoints = ccw ? points : [...points].reverse();

    // Transform points to geometry space
    const geoPoints = orderedPoints.map(
      (p) => new THREE.Vector3(p[horizontal], p[vertical], p[normal]),
    );

    // Find middle segment and its midpoint
    const midSegmentIdx = Math.floor((geoPoints.length - 1) / 2);
    const geoCenter = new THREE.Vector3();
    if (geoPoints.length >= 2) {
      const gMid = geoPoints[midSegmentIdx];
      const gMidNext = geoPoints[midSegmentIdx + 1];
      if (gMid && gMidNext) {
        geoCenter.copy(gMid).add(gMidNext).multiplyScalar(0.5);
      }
    } else if (geoPoints.length === 1) {
      const g0 = geoPoints[0];
      if (g0) geoCenter.copy(g0);
    }

    // Transform center back to WORLD space for position
    // geoCenter = (world[horizontal], world[vertical], world[normal])
    // So: world[horizontal] = geoCenter.x, world[vertical] = geoCenter.y, world[normal] = geoCenter.z
    const finalPosition = new THREE.Vector3();
    finalPosition[horizontal] = geoCenter.x;
    finalPosition[vertical] = geoCenter.y;
    finalPosition[normal] = geoCenter.z;

    // Final rotation calculation
    let baseRotation: THREE.Euler;

    if (side === Sides.TOP || side === Sides.BOTTOM) {
      baseRotation = new THREE.Euler(0, 0, 0);
      baseRotation.x -= Math.PI / 2;
      baseRotation.x += THREE.MathUtils.degToRad(180);
    } else {
      baseRotation = new THREE.Euler(0, 0, 0);
      baseRotation.x -= Math.PI;
      baseRotation.y += THREE.MathUtils.degToRad(180);
      baseRotation.z += Math.PI;
    }

    const finalRotation = baseRotation; // Use calculated rotation

    // Build drag constraint for movement perpendicular to the edge (along cut normal)
    // This allows dragging the profile to change the cut offset
    let dragConstraint: DragConstraint | undefined;
    let settingsContext: Record<string, unknown> | undefined;

    if (cutGroupName && cutNormal) {
      // Calculate bounds along the cut normal direction
      // The profile moves along cutNormal to change the cut offset
      // Minimum offset from border is enforced (default 50mm)
      // Maximum offset is calculated from case geometry

      // The "zero offset" position is where the cut would be at the panel edge
      // We move from there along cutNormal by offset amount
      // So: position = zeroPos + cutNormal * offset
      // zeroPos = edgeCenter - cutNormal * currentCutOffset
      const zeroOffsetPos = edgeCenter
        .clone()
        .sub(cutNormal.clone().multiplyScalar(currentCutOffset));

      // Bounds: from minOffsetFromBorder to maxCutOffset
      // These are positions along the cutNormal direction from zeroOffsetPos
      const boundsAtMinOffset = zeroOffsetPos
        .clone()
        .add(cutNormal.clone().multiplyScalar(minOffsetFromBorder));
      const boundsAtMaxOffset = zeroOffsetPos
        .clone()
        .add(cutNormal.clone().multiplyScalar(maxCutOffset));

      dragConstraint = {
        type: "edge",
        axis: [cutNormal.x, cutNormal.y, cutNormal.z],
        bounds: {
          min: [boundsAtMinOffset.x, boundsAtMinOffset.y, boundsAtMinOffset.z],
          max: [boundsAtMaxOffset.x, boundsAtMaxOffset.y, boundsAtMaxOffset.z],
        },
        snapAnchors: [
          // Current position
          {
            position: [edgeCenter.x, edgeCenter.y, edgeCenter.z],
            label: "Current",
          },
        ],
        sourceOptionUuid: cutGroupName, // Use cutGroupName as identifier
      };

      settingsContext = {
        cutGroupName,
        cutNormal: [cutNormal.x, cutNormal.y, cutNormal.z],
        edgeCenter: [edgeCenter.x, edgeCenter.y, edgeCenter.z],
        zeroOffsetPos: [zeroOffsetPos.x, zeroOffsetPos.y, zeroOffsetPos.z],
        edgeDirection: [edgeDirection.x, edgeDirection.y, edgeDirection.z],
        edgeLength,
        currentCutOffset,
        profileExtra: depProfileExtra,
        cutReference: side,
        minOffsetFromBorder,
        maxCutOffset,
        inverse, // For inverted groups, cutNormal is already negated
      };
    }

    const profileResult = {
      ...restDeps, // Includes partId, type, miter, partSelection
      id: partId,
      points: points.map((p) => p.clone()), // Store world points, renderer centers them
      buildstepName: this.buildstepName,
      group: groupName,
      position: finalPosition.clone(),
      orientation: 0, // Profile orientation handled via rotation
      rotation: finalRotation,
      cutReference, // Keep cutReference if needed by renderer
      inverse, // Add back inverse which was destructured separately
      ...(dragConstraint ? { dragConstraint } : {}),
      ...(settingsContext ? { settingsContext } : {}),
    } satisfies ProfileDefinition & AbstractPartDefinition<string>;

    return profileResult;
  }

  /**
   * Convert a drag position change back to settings updates.
   * For connectors, this updates the margin in the variableOptions.
   * For profiles, this updates the cut offset in settings.cuts.
   * Returns both the settings update and display values for UI feedback.
   */
  public updateSettingsFromDrag(
    partId: string,
    newPosition: THREE.Vector3,
    constraint: DragConstraint,
    settingsContext: Record<string, unknown> | undefined,
    currentSettings: FlightCaseSettings,
    options?: DragUpdateOptions,
  ): DragUpdateResult<FlightCaseSettings> | null {
    if (!constraint.sourceOptionUuid || !settingsContext) {
      return null;
    }

    // Check if this is a profile drag (has cutGroupName in context)
    if (settingsContext.cutGroupName) {
      return this.updateSettingsFromProfileDrag(
        partId,
        newPosition,
        constraint,
        settingsContext,
        currentSettings,
        options,
      );
    }

    // Otherwise, handle connector drag
    return this.updateSettingsFromConnectorDrag(
      partId,
      newPosition,
      constraint,
      settingsContext,
      currentSettings,
      options,
    );
  }

  /**
   * Handle drag updates for profile parts - updates cut.offset
   */
  private updateSettingsFromProfileDrag(
    _partId: string,
    newPosition: THREE.Vector3,
    _constraint: DragConstraint,
    settingsContext: Record<string, unknown>,
    currentSettings: FlightCaseSettings,
    options?: DragUpdateOptions,
  ): DragUpdateResult<FlightCaseSettings> | null {
    const cutGroupName = settingsContext.cutGroupName as string;
    const cutNormal = new THREE.Vector3(
      ...(settingsContext.cutNormal as [number, number, number]),
    );
    const zeroOffsetPos = new THREE.Vector3(
      ...(settingsContext.zeroOffsetPos as [number, number, number]),
    );
    const minOffsetFromBorder =
      (settingsContext.minOffsetFromBorder as number) ?? 50;
    const maxCutOffset = (settingsContext.maxCutOffset as number) ?? 500;

    // Find the cut by group name
    const cutIndex = currentSettings.cuts.findIndex(
      (c) => c.groupName === cutGroupName,
    );

    if (cutIndex === -1) {
      return null;
    }

    const cut = currentSettings.cuts[cutIndex];
    if (!cut) {
      return null;
    }

    // Calculate the new offset based on position along the cut normal
    // newPosition = zeroOffsetPos + cutNormal * newOffset
    // So: newOffset = (newPosition - zeroOffsetPos) . cutNormal
    const movementFromZero = newPosition.clone().sub(zeroOffsetPos);
    const newOffsetRaw = movementFromZero.dot(cutNormal);
    const gridStepMm = gridStepFromOptions(options);

    // Enforce min/max offset bounds, snap to the drag grid, then clamp again so
    // rounding cannot escape the allowed range when bounds are off-grid.
    const newOffset = clampRoundToDragGrid(
      newOffsetRaw,
      minOffsetFromBorder,
      maxCutOffset,
      gridStepMm,
    );
    const snappedPosition = zeroOffsetPos
      .clone()
      .addScaledVector(cutNormal, newOffset);

    // Create updated cuts array
    const updatedCuts = [...currentSettings.cuts];
    updatedCuts[cutIndex] = {
      ...cut,
      offset: newOffset,
    };

    return {
      settingsUpdate: {
        cuts: updatedCuts,
      },
      displayValues: {
        primary: {
          label: "Offset",
          value: newOffset,
          unit: "mm",
        },
      },
      snappedPosition: [
        snappedPosition.x,
        snappedPosition.y,
        snappedPosition.z,
      ],
    };
  }

  /**
   * Handle drag updates for connector parts - updates margin in variableOptions
   */
  private updateSettingsFromConnectorDrag(
    _partId: string,
    newPosition: THREE.Vector3,
    constraint: DragConstraint,
    settingsContext: Record<string, unknown>,
    currentSettings: FlightCaseSettings,
    options?: DragUpdateOptions,
  ): DragUpdateResult<FlightCaseSettings> | null {
    // Find the variable option by UUID
    const optionIndex = currentSettings.variableOptions.findIndex(
      (opt) => opt.uuid === constraint.sourceOptionUuid,
    );

    if (optionIndex === -1) {
      console.warn(
        `Could not find variableOption with UUID ${constraint.sourceOptionUuid}`,
      );
      return null;
    }

    const option = currentSettings.variableOptions[optionIndex];
    if (!option || !isConnectorSetInfos(option)) {
      return null;
    }

    // Extract edge info from settings context. These already carry the
    // reference-edge frame (matching Rect.resolvePosition1d), so the projection
    // below inverts the forward placement directly for every leaf and side.
    const edgeDir = new THREE.Vector3(
      ...(settingsContext.edgeDirection as [number, number, number]),
    );
    const originalEdgeStart = new THREE.Vector3(
      ...(settingsContext.originalEdgeStartPos as [number, number, number]),
    );
    const originalEdgeEnd = new THREE.Vector3(
      ...(settingsContext.originalEdgeEndPos as [number, number, number]),
    );
    const originalEdgeLength = settingsContext.originalEdgeLength as number;
    const n = (settingsContext.n as number) ?? 0;
    // Flat-mirror copies are placed by calcPosition with a flipped margin
    // (CENTER) / swapped START<->END anchor. count>1 is already handled via the
    // stored (flipped) n, but the count===1 branches below assume the
    // unmirrored anchor, so undo the flip there.
    const mirrored = (settingsContext.mirrored as boolean) ?? false;

    // Calculate margins based on alignment using ORIGINAL edge positions

    // Get the current alignment and count from the option
    const alignment = option.alignment;
    const count = option.count;

    // Get padding from cut
    // Access partSelection via option
    // It is stored as partSelection: LockType | HingeType
    const partSelection = option.partSelection;
    const cutVal = (partSelection as Record<string, unknown>).cut;
    const cut = typeof cutVal === "number" ? cutVal : Number(cutVal) || 0;
    const padding = cut / 2;

    const visualOffset = new THREE.Vector3(
      ...(settingsContext.visualOffset as [number, number, number]),
    );
    const placementPoint = newPosition.clone().sub(visualOffset);

    const rawMargin = marginFromConnectorPlacement(placementPoint, {
      originalEdgeStart,
      originalEdgeEnd,
      originalEdgeLength,
      edgeDir,
      count,
      n,
      padding,
      alignment,
      mirrored,
    });
    if (rawMargin === null) {
      return null;
    }

    const gridStepMm = gridStepFromOptions(options);
    const marginRange = computeConnectorMarginRangeFromDragContext(
      constraint,
      settingsContext,
      option,
    );
    const newMargin = marginRange
      ? clampRoundToDragGrid(
          rawMargin,
          marginRange.min,
          marginRange.max,
          gridStepMm,
        )
      : rawMargin;

    const edgeStartPos = new THREE.Vector3(
      ...(settingsContext.edgeStartPos as [number, number, number]),
    );
    const edgeEndPos = new THREE.Vector3(
      ...(settingsContext.edgeEndPos as [number, number, number]),
    );
    const placementOnRefEdge = connectorPositionFromMargin(newMargin, {
      edgeDir,
      originalEdgeStart,
      originalEdgeEnd,
      originalEdgeLength,
      n,
      padding,
      alignment,
      count,
      mirrored,
    });
    const anchorOnDragEdge = new THREE.Vector3();
    new THREE.Line3(edgeStartPos, edgeEndPos).closestPointToPoint(
      placementOnRefEdge,
      true,
      anchorOnDragEdge,
    );
    const snappedPosition = anchorOnDragEdge.clone().add(visualOffset);

    // Create updated variable options array
    const updatedVariableOptions = [...currentSettings.variableOptions];
    updatedVariableOptions[optionIndex] = {
      ...option,
      margin: newMargin,
    } as VariableOptions;

    return {
      settingsUpdate: {
        variableOptions: updatedVariableOptions,
      },
      displayValues: {
        primary: {
          label: "Margin",
          value: newMargin,
          unit: "mm",
        },
      },
      snappedPosition: [
        snappedPosition.x,
        snappedPosition.y,
        snappedPosition.z,
      ],
    };
  }

  /**
   * Derive the achievable margin range for a connector set from blueprint edge
   * geometry — independent of whether connectors currently resolve onto the
   * edge (e.g. margin pushed them off so no drag part was emitted).
   */
  public computeMarginRangesForConnectorOption(
    blueprint: FlightCaseBlueprint,
    settings: FlightCaseSettings,
    connector: ConnectorSetInfos,
  ): ConnectorMarginAxisRange | null {
    const angleSectionDepth =
      settings.exteriorOptions.angleSections?.depth ?? 0;
    const slots = compileConnectorSet(connector);
    let combined: ConnectorMarginAxisRange | null = null;

    for (const slot of slots) {
      const edges = ProfileBuildStep.findEdgesForConnector(
        blueprint,
        slot.group,
        slot.side,
      );
      for (const edge of edges) {
        const range = ProfileBuildStep.probeConnectorMarginRange(
          edge,
          slot,
          connector,
          angleSectionDepth,
        );
        if (!range) {
          continue;
        }
        combined = combined
          ? intersectConnectorMarginRanges([combined, range])
          : range;
      }
    }

    return combined;
  }

  private static findEdgesForConnector(
    blueprint: FlightCaseBlueprint,
    group: string,
    side: Sides,
  ): EdgeInfos[] {
    const edges: EdgeInfos[] = [];
    for (const rect of blueprint.rects) {
      for (const edge of rect.edges) {
        if (
          edge.type === EdgeType.FROMCUT &&
          edge.cutName === group &&
          edge.sides[0] === side
        ) {
          edges.push(edge);
        }
      }
    }
    return edges;
  }

  private static probeConnectorMarginRange(
    edge: EdgeInfos,
    slot: ConnectorInfos,
    option: ConnectorSetInfos,
    angleSectionDepth: number,
  ): ConnectorMarginAxisRange | null {
    const firstRect = edge.rects[0];
    if (!firstRect) {
      return null;
    }

    const refIsVertical = edge.orientationOnRect === OrientationOnRect.VERTICAL;
    const refEdge =
      firstRect.edges[refIsVertical ? EdgeIndex.LEFT : EdgeIndex.BOTTOM];
    const refDir = (
      refIsVertical
        ? firstRect.properties.verticalDir
        : firstRect.properties.horizontalDir
    ).clone();
    const refAligned = refEdge ? refEdge.direction.dot(refDir) > 0 : true;
    const refStartEndpoint = refAligned ? refEdge?.start : refEdge?.end;
    const refEndEndpoint = refAligned ? refEdge?.end : refEdge?.start;
    const refOriginalStartPos =
      refStartEndpoint?.originalOuterPos.clone() ??
      edge.start.originalOuterPos.clone();
    const refOriginalEndPos =
      refEndEndpoint?.originalOuterPos.clone() ??
      edge.end.originalOuterPos.clone();
    const refOriginalEdgeLength =
      refEdge?.originalEdgeLength ?? edge.originalEdgeLength;

    const cutRaw = (option.partSelection as Record<string, unknown>).cut;
    const connectorCut =
      typeof cutRaw === "number" ? cutRaw : Number(cutRaw) || 0;
    const totalPadding = angleSectionDepth + connectorCut / 2;

    const edgeStartPos = edge.start.outerPos;
    const edgeEndPos = edge.end.outerPos;
    const edgeDirection = edge.direction.clone();
    const paddedEdgeStart = edgeStartPos
      .clone()
      .add(edgeDirection.clone().multiplyScalar(totalPadding));
    const paddedEdgeEnd = edgeEndPos
      .clone()
      .sub(edgeDirection.clone().multiplyScalar(totalPadding));

    const constraint: Pick<DragConstraint, "type" | "bounds"> = {
      type: "edge",
      bounds: {
        min: [paddedEdgeStart.x, paddedEdgeStart.y, paddedEdgeStart.z],
        max: [paddedEdgeEnd.x, paddedEdgeEnd.y, paddedEdgeEnd.z],
      },
    };

    const settingsContext: Record<string, unknown> = {
      originalEdgeStartPos: [
        refOriginalStartPos.x,
        refOriginalStartPos.y,
        refOriginalStartPos.z,
      ],
      originalEdgeEndPos: [
        refOriginalEndPos.x,
        refOriginalEndPos.y,
        refOriginalEndPos.z,
      ],
      originalEdgeLength: refOriginalEdgeLength,
      edgeDirection: [refDir.x, refDir.y, refDir.z],
      n: slot.position.n,
      mirrored: slot.mirrored,
    };

    return computeConnectorMarginRangeFromDragContext(
      constraint,
      settingsContext,
      option,
    );
  }
}
